import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    UploadSimple,
    ArrowsClockwise,
    Sparkle,
    Warning,
    Info,
    Trophy,
    Target as TargetIcon,
    ChartBar,
    Download,
    Binoculars,
    X,
    MapPin,
    ArrowSquareOut,
    Copy,
    TrendUp,
    TrendDown,
    Minus,
} from "@phosphor-icons/react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    ResponsiveContainer,
    Tooltip as RTooltip,
    LabelList,
    Cell,
} from "recharts";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    auctionInsightsAi,
    auctionInsightsFetch,
    rivalAdCopyScrape,
    keywordPlannerGeoSuggest,
    generateAdVariations, errText,
} from "@/lib/api";
import { pushAdClipboard } from "@/lib/adClipboard";
import { AdsImportBar } from "@/components/AdsImportBar";

// ----- Column name normalizer -----
// Google Ads exports vary slightly. Map common header variants to internal keys.
const HEADER_MAP = {
    "display url domain": "domain",
    "domain": "domain",
    "impression share": "impression_share",
    "impr. share": "impression_share",
    "search impr. share": "impression_share",
    "search impression share": "impression_share",
    "overlap rate": "overlap_rate",
    "position above rate": "position_above_rate",
    "top of page rate": "top_of_page_rate",
    "search top impression rate": "top_of_page_rate",
    "abs. top of page rate": "abs_top_of_page_rate",
    "absolute top of page rate": "abs_top_of_page_rate",
    "search abs. top impression rate": "abs_top_of_page_rate",
    "outranking share": "outranking_share",
};

// Parse a percentage cell — "22.15%", "--", "< 10%", "> 90%".
function parsePct(v) {
    if (v == null) return null;
    const s = String(v).trim();
    if (!s || s === "--" || s.toLowerCase() === "n/a") return null;
    if (s.startsWith("<")) {
        const n = parseFloat(s.replace(/[^0-9.]/g, ""));
        return isNaN(n) ? null : Math.max(0, n - 5);
    }
    if (s.startsWith(">")) {
        const n = parseFloat(s.replace(/[^0-9.]/g, ""));
        return isNaN(n) ? null : Math.min(100, n + 5);
    }
    const n = parseFloat(s.replace(/[^0-9.\-]/g, ""));
    return isNaN(n) ? null : n;
}

// Split a CSV line respecting quoted commas.
function splitCsv(line) {
    const out = [];
    let cur = "";
    let q = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
            if (q && line[i + 1] === '"') {
                cur += '"';
                i++;
            } else {
                q = !q;
            }
        } else if (ch === "," && !q) {
            out.push(cur);
            cur = "";
        } else {
            cur += ch;
        }
    }
    out.push(cur);
    return out.map((s) => s.trim());
}

// Parse a Google Ads Auction Insights CSV text export. Returns { scope, rows[], you_label }.
export function parseAuctionInsightsCsv(text) {
    // Google exports \r\n or \r on Windows.
    const lines = text.split(/\r?\n/);
    let headerIdx = -1;
    let scope = "";
    for (let i = 0; i < lines.length; i++) {
        const l = lines[i].toLowerCase();
        if (!scope && (l.startsWith('"campaign') || l.startsWith("campaign:"))) {
            scope = lines[i].replace(/^"+|"+$/g, "").replace(/^campaign:\s*/i, "").trim();
        }
        if (l.includes("display url domain") && l.includes("impression share") ||
            l.includes("display url domain") && l.includes("impr. share")) {
            headerIdx = i;
            break;
        }
    }
    if (headerIdx < 0) {
        throw new Error(
            'Could not find header row. Expected columns: "Display URL domain" + "Impression share" (or "Impr. share").'
        );
    }
    const rawHeaders = splitCsv(lines[headerIdx]);
    const keys = rawHeaders.map((h) => HEADER_MAP[h.toLowerCase()] || null);
    const rows = [];
    let youLabel = "You";
    for (let i = headerIdx + 1; i < lines.length; i++) {
        const line = lines[i];
        if (!line.trim()) break; // blank line = section end
        const cells = splitCsv(line);
        if (cells.length < 2) continue;
        const row = {};
        cells.forEach((c, idx) => {
            const key = keys[idx];
            if (!key) return;
            if (key === "domain") row[key] = c.trim();
            else row[key] = parsePct(c);
        });
        if (!row.domain) continue;
        if (row.domain.toLowerCase() === "you" || row.domain.toLowerCase() === "you*") {
            youLabel = row.domain;
        }
        rows.push(row);
    }
    if (!rows.length) throw new Error("No data rows found in the CSV.");
    return { scope, rows, you_label: youLabel };
}

const currency = (n) => (typeof n === "number" ? n.toFixed(1) + "%" : "—");

export const AuctionInsightsAuditor = ({ preloadedAudit, onAuditSaved } = {}) => {
    const fileRef = useRef(null);
    // "This account's API access does not include Auction Insights metrics"
    // — once we hit that 422 from /ads/auction-insights/fetch, remember it
    // so the CSV upload becomes the primary CTA and the live-fetch button
    // stays disabled with a tooltip. Cleared when the user loads a fresh
    // audit or switches accounts.
    const [apiAccessRestricted, setApiAccessRestricted] = useState(false);
    const [apiAccessMessage, setApiAccessMessage] = useState("");
    const [rows, setRows] = useState([]);
    const [scope, setScope] = useState("");
    const [youLabel, setYouLabel] = useState("You");
    const [error, setError] = useState("");
    const [aiLoading, setAiLoading] = useState(false);
    const [ai, setAi] = useState(null);
    const [filename, setFilename] = useState("");
    const [snoopDomain, setSnoopDomain] = useState("");
    const [delta, setDelta] = useState(null);

    // Rehydrate when opened from Search History.
    useEffect(() => {
        const p = preloadedAudit?.audit;
        if (!p) return;
        setRows(Array.isArray(p.rows) ? p.rows : []);
        setScope(p.scope || "");
        setYouLabel(p.you_label || "You");
        setFilename(p.filename || "(from history)");
        setAi(p.ai || null);
        setDelta(null); // deltas apply only to fresh AI runs
        setError("");
    }, [preloadedAudit?.key, preloadedAudit?.audit]);

    const handleFile = async (file) => {
        if (!file) return;
        setError("");
        setAi(null);
        setDelta(null);
        try {
            const text = await file.text();
            const parsed = parseAuctionInsightsCsv(text);
            setRows(parsed.rows);
            setScope(parsed.scope || file.name.replace(/\.csv$/i, ""));
            setYouLabel(parsed.you_label || "You");
            setFilename(file.name);
            toast.success(`Parsed ${parsed.rows.length} rows`);
        } catch (e) {
            setError(e.message || "Failed to parse the CSV");
            setRows([]);
            setScope("");
            setFilename(file.name);
            toast.error(e.message || "Parse failed");
        }
    };

    // Pull Auction Insights directly from Google Ads API — bypasses the CSV
    // upload path. The API returns rows in the same shape as the CSV parser,
    // with a synthetic "You" row already prepended if the account had
    // impression-share data in the selected scope.
    //
    // If Google returns "developer doesn't have access to metrics" (mapped
    // to HTTP 422 on our backend), we flip `apiAccessRestricted` so the UI
    // permanently downgrades the "Fetch from API" button and promotes the
    // CSV-upload path — no code change on our side can unlock those metrics.
    const handleGoogleFetch = async (customerId, dateRange, account, campaignIds) => {
        setError("");
        setAi(null);
        setDelta(null);
        try {
            const data = await auctionInsightsFetch({
                customer_id: customerId,
                date_range: dateRange,
                campaign_ids: Array.isArray(campaignIds) ? campaignIds : [],
            });
            if (!data.rows?.length) {
                setRows([]);
                setError(
                    "Google Ads returned no Auction Insights rows for the selected scope. Try a wider date range or a campaign with meaningful traffic."
                );
                return;
            }
            setRows(data.rows);
            const scopeLabel = data.scope || account?.name || "Google Ads";
            setScope(scopeLabel);
            setYouLabel("You");
            setFilename(
                `Google Ads · ${dateRange.replace(/_/g, " ").toLowerCase()} · ${scopeLabel}`
            );
            // Live API worked — clear any lingering restriction banner.
            setApiAccessRestricted(false);
            setApiAccessMessage("");
        } catch (e) {
            const status = e?.response?.status;
            const detail =
                errText(e, "Google Ads fetch failed");
            if (status === 422) {
                // Metric-level access denied — remember it, and coach the
                // user toward the CSV path.
                setApiAccessRestricted(true);
                setApiAccessMessage(
                    typeof detail === "string" ? detail : String(detail)
                );
                setError("");
                setRows([]);
                toast.warning("Live fetch unavailable — use CSV upload", {
                    description:
                        typeof detail === "string" ? detail : String(detail),
                    duration: 12000,
                });
                // Do NOT rethrow — swallow so AdsImportBar doesn't stack a
                // second error toast on top.
                return;
            }
            setError(typeof detail === "string" ? detail : String(detail));
            setRows([]);
            throw e; // let AdsImportBar show the toast
        }
    };

    const runAi = async () => {
        if (rows.length < 2) {
            toast.error("Need at least 2 rows (You + one rival)");
            return;
        }
        setAiLoading(true);
        const t = toast.loading("Claude analysing your Auction Insights…");
        try {
            const data = await auctionInsightsAi({
                campaign_or_scope: scope,
                you_label: youLabel,
                rows,
                filename,
            });
            const { delta_vs_previous, audit_id, ...verdict } = data;
            setAi(verdict);
            setDelta(delta_vs_previous || null);
            onAuditSaved?.();
            toast.success(
                delta_vs_previous ? "Verdict ready · compared to last scan" : "Verdict ready · saved to history",
                { id: t }
            );
        } catch (e) {
            const msg = errText(e, "AI failed");
            toast.error(msg, { id: t });
        } finally {
            setAiLoading(false);
        }
    };

    // Sort rows by impression share desc for the table + chart.
    const sortedRows = useMemo(() => {
        return [...rows].sort(
            (a, b) => (b.impression_share || 0) - (a.impression_share || 0)
        );
    }, [rows]);

    const youRow = useMemo(
        () => sortedRows.find((r) => (r.domain || "").toLowerCase().startsWith("you")),
        [sortedRows]
    );
    const rivalRows = useMemo(
        () =>
            sortedRows
                .filter((r) => !(r.domain || "").toLowerCase().startsWith("you"))
                .slice(0, 3),
        [sortedRows]
    );

    const chartData = useMemo(() => {
        const list = [];
        if (youRow) list.push({ ...youRow, tag: "You" });
        rivalRows.forEach((r) => list.push({ ...r, tag: "Rival" }));
        return list.map((r) => ({
            name: (r.domain || "").length > 22
                ? r.domain.slice(0, 20) + "…"
                : r.domain,
            share: r.impression_share || 0,
            tag: r.tag,
        }));
    }, [youRow, rivalRows]);

    const exportCsv = () => {
        if (!ai) return;
        const rowsOut = [
            ["Metric", "Value"],
            ["Scope", scope || "—"],
            ["Your Impression Share", ai.you_impression_share ?? "—"],
            ["Market Leader", ai.market_leader?.domain || "—"],
            ["Market Leader IS", ai.market_leader?.impression_share ?? "—"],
            ["Gap vs You", ai.market_leader?.gap_vs_you ?? "—"],
            ["Direct Rival", ai.direct_rival?.domain || "—"],
            ["Direct Rival Overlap", ai.direct_rival?.overlap_rate ?? "—"],
            ["Primary Lever", ai.primary_lever || "—"],
            ["Lever Reasoning", ai.lever_reasoning || ""],
            ["Gap Summary", ai.gap_summary || ""],
            ...(ai.action_items || []).map((a, i) => [`Action ${i + 1}`, a]),
        ];
        const esc = (v) => {
            const s = v == null ? "" : String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const csv = rowsOut.map((r) => r.map(esc).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `auction-insights-verdict-${(scope || "report").replace(/[^\w-]+/g, "_").slice(0, 40)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="flex flex-col flex-1 overflow-hidden bg-white">
            <div className="border-b border-neutral-300 px-6 py-4 space-y-3 bg-neutral-50">
                {apiAccessRestricted && (
                    <div
                        data-testid="ai-api-restricted-banner"
                        className="border-2 border-amber-500 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-3"
                    >
                        <Warning
                            size={18}
                            weight="bold"
                            className="text-amber-600 mt-0.5 shrink-0"
                        />
                        <div className="flex-1">
                            <div className="font-heading font-black uppercase tracking-tight text-amber-900">
                                Live fetch unavailable for this account
                            </div>
                            <p className="mt-1 leading-relaxed">
                                {apiAccessMessage ||
                                    "This Google Ads account's API access does not include Auction Insights metrics."}
                            </p>
                            <p className="mt-2 text-xs">
                                In Google Ads → Reports →{" "}
                                <b>Auction Insights</b> → Download as CSV, then
                                upload the file below to run the AI verdict.
                            </p>
                        </div>
                        <button
                            data-testid="ai-restricted-upload"
                            onClick={() => fileRef.current?.click()}
                            className="brutal-btn h-9 px-3 border border-ink bg-ink text-white hover:bg-klein hover:border-klein font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 shrink-0"
                        >
                            <UploadSimple size={12} weight="bold" />
                            Upload CSV
                        </button>
                    </div>
                )}

                {/* PRIMARY path — CSV upload */}
                <div className="border border-ink bg-white p-4 flex items-center gap-3 flex-wrap">
                    <ChartBar size={18} weight="bold" className="text-klein" />
                    <div className="flex-1 min-w-[220px]">
                        <div className="font-heading font-black uppercase tracking-tight text-sm text-ink">
                            Upload Auction Insights CSV
                        </div>
                        <div className="text-[11px] font-mono text-neutral-600 mt-0.5">
                            In Google Ads → Reports →{" "}
                            <span className="text-ink font-bold">
                                Auction Insights
                            </span>{" "}
                            → Download as CSV, then upload it here.
                        </div>
                    </div>
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".csv,text/csv"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        className="hidden"
                        data-testid="ai-csv-input"
                    />
                    <button
                        data-testid="ai-csv-upload"
                        onClick={() => fileRef.current?.click()}
                        className="brutal-btn h-10 px-4 border border-klein bg-klein text-white hover:bg-ink hover:border-ink font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-2"
                    >
                        <UploadSimple size={14} weight="bold" />{" "}
                        {filename ? "Replace CSV" : "Upload CSV"}
                    </button>
                    {rows.length > 0 && (
                        <button
                            data-testid="ai-run"
                            onClick={runAi}
                            disabled={aiLoading}
                            className="brutal-btn h-10 px-3 border border-ink bg-ink text-white font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-50"
                        >
                            {aiLoading ? (
                                <ArrowsClockwise
                                    size={11}
                                    weight="bold"
                                    className="animate-spin"
                                />
                            ) : (
                                <Sparkle size={11} weight="bold" />
                            )}
                            Analyse with AI
                        </button>
                    )}
                </div>

                {/* SECONDARY path — direct API fetch. Disabled if the account
                    lacks Auction Insights metric access (with tooltip). */}
                <details
                    className="border border-neutral-300 bg-white"
                    open={!apiAccessRestricted}
                >
                    <summary
                        data-testid="ai-live-fetch-toggle"
                        className={`cursor-pointer select-none px-4 py-2 flex items-center gap-2 text-xs font-mono uppercase tracking-widest font-bold ${
                            apiAccessRestricted
                                ? "text-neutral-400"
                                : "text-neutral-700 hover:bg-neutral-50"
                        }`}
                        title={
                            apiAccessRestricted
                                ? "Requires Google Ads API access to Auction Insights metrics — not granted for this account."
                                : "Pull Auction Insights directly from Google Ads (requires API access to auction_insight_* metrics)."
                        }
                    >
                        <Info size={11} weight="bold" />
                        Or fetch live from Google Ads API
                        {apiAccessRestricted && (
                            <span className="ml-2 border border-neutral-400 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest text-neutral-500">
                                Restricted
                            </span>
                        )}
                    </summary>
                    <div
                        className={`p-3 border-t border-neutral-200 ${
                            apiAccessRestricted
                                ? "opacity-50 pointer-events-none"
                                : ""
                        }`}
                    >
                        <AdsImportBar
                            reportKind="auction-insights"
                            onFetch={handleGoogleFetch}
                        />
                    </div>
                </details>

                {filename && (
                    <div className="text-[11px] font-mono text-neutral-500 flex items-center gap-2">
                        <Info size={11} />
                        <span>
                            Loaded:{" "}
                            <span className="text-ink font-bold">{filename}</span>
                            {scope && (
                                <>
                                    {" · "}scope:{" "}
                                    <span className="text-ink font-bold">
                                        {scope}
                                    </span>
                                </>
                            )}
                            {rows.length > 0 && (
                                <>
                                    {" · "}
                                    <span className="text-ink font-bold">
                                        {rows.length}
                                    </span>{" "}
                                    rows
                                </>
                            )}
                        </span>
                    </div>
                )}
                {error && (
                    <div
                        data-testid="ai-parse-error"
                        className="border border-amber-500 bg-amber-50 px-3 py-2 text-xs font-mono text-amber-900 flex items-start gap-2"
                    >
                        <Warning size={12} weight="bold" className="mt-0.5" />
                        {error}
                    </div>
                )}
            </div>

            <div className="flex-1 overflow-y-auto thin-scroll">
                {rows.length === 0 ? (
                    <EmptyAi />
                ) : (
                    <div className="p-6 space-y-6">
                        {delta && <AuctionDeltaBanner delta={delta} />}
                        {/* Chart + AI verdict block */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            <div className="lg:col-span-2 border border-neutral-300 bg-white">
                                <div className="px-4 py-2 border-b border-neutral-300 bg-neutral-50 flex items-center gap-2">
                                    <ChartBar size={13} weight="bold" className="text-klein" />
                                    <span className="mono-label">You vs top 3 rivals — Impression Share</span>
                                </div>
                                <div className="p-4" style={{ height: 260 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 40 }}>
                                            <XAxis type="number" hide domain={[0, 100]} />
                                            <YAxis
                                                type="category"
                                                dataKey="name"
                                                tick={{ fill: "#1a1a1a", fontFamily: "monospace", fontSize: 11 }}
                                                width={150}
                                            />
                                            <RTooltip
                                                formatter={(v) => `${v.toFixed(1)}%`}
                                                contentStyle={{
                                                    border: "2px solid #1a1a1a",
                                                    borderRadius: 0,
                                                    fontFamily: "monospace",
                                                    fontSize: 12,
                                                }}
                                            />
                                            <Bar dataKey="share" name="Impression share (%)" barSize={26}>
                                                {chartData.map((d, i) => (
                                                    <Cell
                                                        key={i}
                                                        fill={d.tag === "You" ? "#0000ff" : "#525252"}
                                                    />
                                                ))}
                                                <LabelList
                                                    dataKey="share"
                                                    position="right"
                                                    formatter={(v) => `${v.toFixed(1)}%`}
                                                    style={{ fill: "#1a1a1a", fontFamily: "monospace", fontSize: 11, fontWeight: 700 }}
                                                />
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                            {ai ? (
                                <VerdictCard ai={ai} onExport={exportCsv} />
                            ) : (
                                <div className="border border-dashed border-neutral-300 bg-neutral-50 p-4 text-xs font-mono text-neutral-500">
                                    Click <span className="font-bold text-ink">Analyse with AI</span> to get the Market Leader,
                                    Direct Rival and lever recommendation.
                                </div>
                            )}
                        </div>

                        {/* Table */}
                        <RivalTable rows={sortedRows} onSnoop={setSnoopDomain} defaultKeyword={scope} />
                    </div>
                )}
            </div>
            <SnoopModal
                domain={snoopDomain}
                onClose={() => setSnoopDomain("")}
                defaultKeyword={scope}
            />
        </div>
    );
};

const EmptyAi = () => (
    <div className="flex-1 flex items-center justify-center p-10 text-center">
        <div className="max-w-lg">
            <div className="mono-label mb-3">// auction insights</div>
            <h2 className="font-display text-3xl font-black tracking-tighter text-ink leading-tight">
                Client vs rivals, <br />
                <span className="text-klein">side-by-side.</span>
            </h2>
            <p className="text-sm text-neutral-600 mt-4 leading-relaxed">
                Export the Auction Insights report from Google Ads UI (Campaign → Insights → Auction insights →
                Download CSV) and drop it here. We&apos;ll parse the six metrics — Impression Share,
                Overlap Rate, Outranking Share, Top-of-Page Rate, Abs. Top Rate, Position Above Rate —
                then Claude identifies the Market Leader, Direct Rival, and recommends whether to
                spend more or improve Quality Score.
            </p>
        </div>
    </div>
);

const LEVER_STYLES = {
    "Increase budget": "bg-klein text-white border-klein",
    "Improve Quality Score": "bg-amber-500 text-white border-amber-500",
    Both: "bg-red-700 text-white border-red-700",
};

const VerdictCard = ({ ai, onExport }) => {
    const leverCls =
        LEVER_STYLES[ai.primary_lever] || "bg-neutral-800 text-white border-neutral-800";
    return (
        <div className="border-2 border-ink bg-white p-4 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
                <span
                    data-testid="ai-lever"
                    className={`px-2 py-1 text-[11px] font-mono uppercase tracking-widest font-bold border ${leverCls}`}
                >
                    {ai.primary_lever || "—"}
                </span>
                <button
                    onClick={onExport}
                    className="ml-auto brutal-btn h-7 px-2 border border-ink bg-white text-ink hover:bg-ink hover:text-white font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1"
                >
                    <Download size={11} weight="bold" /> CSV
                </button>
            </div>
            <p className="text-xs font-mono text-ink italic">{ai.lever_reasoning}</p>
            <div className="grid grid-cols-2 gap-2">
                <div className="border border-amber-500 bg-amber-50 p-2">
                    <div className="mono-label text-amber-900 flex items-center gap-1">
                        <Trophy size={10} weight="bold" /> Market Leader
                    </div>
                    <div className="font-heading text-sm font-black text-ink truncate">
                        {ai.market_leader?.domain || "—"}
                    </div>
                    <div className="text-[10px] font-mono text-amber-900">
                        {ai.market_leader?.impression_share != null
                            ? `IS ${ai.market_leader.impression_share.toFixed(1)}%`
                            : "—"}
                        {ai.market_leader?.gap_vs_you != null && (
                            <> · +{ai.market_leader.gap_vs_you.toFixed(1)}% vs you</>
                        )}
                    </div>
                </div>
                <div className="border border-red-500 bg-red-50 p-2">
                    <div className="mono-label text-red-900 flex items-center gap-1">
                        <TargetIcon size={10} weight="bold" /> Direct Rival
                    </div>
                    <div className="font-heading text-sm font-black text-ink truncate">
                        {ai.direct_rival?.domain || "—"}
                    </div>
                    <div className="text-[10px] font-mono text-red-900">
                        {ai.direct_rival?.overlap_rate != null
                            ? `Overlap ${ai.direct_rival.overlap_rate.toFixed(1)}%`
                            : "—"}
                    </div>
                </div>
            </div>
            {ai.gap_summary && (
                <p className="text-xs text-ink border-t border-neutral-200 pt-2">
                    {ai.gap_summary}
                </p>
            )}
            {(ai.action_items || []).length > 0 && (
                <div>
                    <div className="mono-label mb-1">Action items — this week</div>
                    <ol className="text-xs text-ink space-y-1 list-decimal list-inside">
                        {ai.action_items.map((a, i) => (
                            <li key={i}>{a}</li>
                        ))}
                    </ol>
                </div>
            )}
        </div>
    );
};

const RivalTable = ({ rows, onSnoop }) => (
    <div className="border border-neutral-300 overflow-hidden">
        <div className="max-h-96 overflow-y-auto thin-scroll">
            <table className="w-full text-sm">
                <thead className="bg-neutral-50 sticky top-0">
                    <tr className="border-b border-neutral-300">
                        <th className="px-3 py-2 text-left mono-label">Domain</th>
                        <th className="px-3 py-2 text-right mono-label">Impr. Share</th>
                        <th className="px-3 py-2 text-right mono-label">Overlap</th>
                        <th className="px-3 py-2 text-right mono-label">Outranking</th>
                        <th className="px-3 py-2 text-right mono-label">Top of Page</th>
                        <th className="px-3 py-2 text-right mono-label">Abs. Top</th>
                        <th className="px-3 py-2 text-right mono-label">Pos. Above</th>
                        <th className="px-3 py-2 text-center mono-label w-20">Snoop</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => {
                        const isYou = (r.domain || "").toLowerCase().startsWith("you");
                        return (
                            <tr
                                key={r.domain}
                                className={`border-b border-neutral-200 ${
                                    isYou ? "bg-klein/5 font-bold" : "hover:bg-neutral-50"
                                }`}
                                data-testid={`ai-row-${r.domain}`}
                            >
                                <td className="px-3 py-1.5 text-ink">
                                    {isYou && (
                                        <span className="mr-2 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest font-bold bg-klein text-white">
                                            You
                                        </span>
                                    )}
                                    {r.domain}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono text-ink">
                                    {currency(r.impression_share)}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono text-ink">
                                    {currency(r.overlap_rate)}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono text-ink">
                                    {currency(r.outranking_share)}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono text-ink">
                                    {currency(r.top_of_page_rate)}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono text-ink">
                                    {currency(r.abs_top_of_page_rate)}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono text-ink">
                                    {currency(r.position_above_rate)}
                                </td>
                                <td className="px-3 py-1.5 text-center">
                                    {!isYou && (
                                        <button
                                            data-testid={`ai-snoop-${r.domain}`}
                                            onClick={() => onSnoop?.(r.domain)}
                                            className="brutal-btn h-6 px-2 border border-ink bg-white text-ink hover:bg-ink hover:text-white font-mono uppercase tracking-widest text-[9px] font-bold flex items-center gap-1 mx-auto"
                                            title="Snoop this competitor's live ad copy"
                                        >
                                            <Binoculars size={10} weight="bold" /> Snoop
                                        </button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    </div>
);

// ---- Snoop Modal ----
const SnoopModal = ({ domain, onClose, defaultKeyword }) => {
    const open = !!domain;
    const [keyword, setKeyword] = useState("");
    const [locQuery, setLocQuery] = useState("");
    const [locSuggestions, setLocSuggestions] = useState([]);
    const [locLoading, setLocLoading] = useState(false);
    const [locOpen, setLocOpen] = useState(false);
    const [selectedLoc, setSelectedLoc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [country, setCountry] = useState("GB");

    useEffect(() => {
        if (open) {
            // Pre-fill keyword from scope if it looks like one; otherwise clear.
            const hint = (defaultKeyword || "").replace(/^campaign:\s*/i, "").trim();
            setKeyword(hint.length <= 60 ? hint : "");
            setResult(null);
            setLocQuery("");
            setSelectedLoc(null);
        }
    }, [open, defaultKeyword]);

    useEffect(() => {
        const q = locQuery.trim();
        if (q.length < 2) {
            setLocSuggestions([]);
            return;
        }
        let cancelled = false;
        setLocLoading(true);
        const t = setTimeout(async () => {
            try {
                const r = await keywordPlannerGeoSuggest(q, country, "en");
                if (!cancelled) {
                    setLocSuggestions(r.suggestions || []);
                    setLocOpen(true);
                }
            } catch (e) {
                if (!cancelled) setLocSuggestions([]);
            } finally {
                if (!cancelled) setLocLoading(false);
            }
        }, 350);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [locQuery, country]);

    const runScan = async () => {
        if (!keyword.trim()) {
            toast.error("Enter a keyword to scan the SERP for");
            return;
        }
        if (!selectedLoc) {
            toast.error("Pick a location");
            return;
        }
        setLoading(true);
        const t = toast.loading("Scanning live SERP for their ads…");
        try {
            const r = await rivalAdCopyScrape({
                domain,
                keyword: keyword.trim(),
                location_name: selectedLoc.canonical_name || selectedLoc.name,
                language_code: "en",
            });
            setResult(r);
            toast.success(
                r.paid_ads?.length
                    ? `Found ${r.paid_ads.length} live paid ad(s)`
                    : "No paid ads showing right now — organic match only",
                { id: t }
            );
        } catch (e) {
            const msg = errText(e, "Scan failed");
            toast.error(msg, { id: t });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl rounded-none border-2 border-ink p-0 bg-white">
                <DialogHeader className="p-4 border-b-2 border-ink">
                    <DialogTitle className="font-display text-xl font-black tracking-tighter text-ink flex items-center gap-2">
                        <Binoculars size={18} weight="bold" className="text-klein" />
                        Snoop: {domain}
                    </DialogTitle>
                    <p className="text-[11px] font-mono text-neutral-500 mt-1">
                        Live SERP scan via DataForSEO · filters ads to this competitor domain
                    </p>
                </DialogHeader>
                <div className="p-4 space-y-3 border-b border-neutral-300 bg-neutral-50">
                    <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-6">
                            <label className="mono-label mb-1 block">Keyword *</label>
                            <input
                                data-testid="snoop-keyword"
                                value={keyword}
                                onChange={(e) => setKeyword(e.target.value)}
                                placeholder="e.g. washing machine repair"
                                className="w-full h-9 text-sm font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-3"
                            />
                        </div>
                        <div className="col-span-4 relative">
                            <label className="mono-label mb-1 block flex items-center gap-1">
                                <MapPin size={10} weight="bold" /> Location *
                                {selectedLoc && (
                                    <span className="ml-auto text-[10px] font-mono normal-case text-klein truncate max-w-[65%]">
                                        {selectedLoc.canonical_name || selectedLoc.name}
                                    </span>
                                )}
                            </label>
                            <input
                                data-testid="snoop-location"
                                value={locQuery}
                                onChange={(e) => setLocQuery(e.target.value)}
                                onFocus={() => setLocOpen(true)}
                                onBlur={() => setTimeout(() => setLocOpen(false), 200)}
                                placeholder="City / postcode"
                                className="w-full h-9 text-sm font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-3"
                            />
                            {locLoading && (
                                <ArrowsClockwise
                                    size={11}
                                    weight="bold"
                                    className="absolute right-3 bottom-2.5 text-neutral-400 animate-spin"
                                />
                            )}
                            {locOpen && locSuggestions.length > 0 && (
                                <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-ink shadow-lg max-h-56 overflow-y-auto">
                                    {locSuggestions.map((s) => (
                                        <button
                                            key={s.id}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                setSelectedLoc(s);
                                                setLocQuery("");
                                                setLocSuggestions([]);
                                                setLocOpen(false);
                                            }}
                                            className="w-full text-left px-3 py-2 text-xs font-mono hover:bg-klein hover:text-white flex items-center justify-between gap-3 border-b border-neutral-200 last:border-b-0"
                                        >
                                            <span className="truncate">{s.canonical_name || s.name}</span>
                                            <span className="text-[10px] uppercase tracking-widest shrink-0 opacity-70">
                                                {s.target_type}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="col-span-2">
                            <label className="mono-label mb-1 block">Country</label>
                            <select
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                className="w-full h-9 text-xs font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-2"
                            >
                                {["GB", "US", "IN", "AU", "CA", "IE", "NZ", "SG", "AE", "ZA"].map((c) => (
                                    <option key={c} value={c}>
                                        {c}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <button
                        data-testid="snoop-run"
                        onClick={runScan}
                        disabled={loading || !keyword.trim() || !selectedLoc}
                        className="brutal-btn h-9 px-4 border border-klein bg-klein text-white hover:bg-klein/90 font-mono uppercase tracking-widest text-[11px] font-bold flex items-center gap-2 disabled:opacity-50"
                    >
                        {loading ? (
                            <ArrowsClockwise size={12} weight="bold" className="animate-spin" />
                        ) : (
                            <Binoculars size={12} weight="bold" />
                        )}
                        Scan live SERP
                    </button>
                </div>
                <div className="p-4 max-h-[420px] overflow-y-auto thin-scroll">
                    {!result ? (
                        <div className="text-xs font-mono text-neutral-500">
                            Enter the keyword this rival is likely bidding on, pick the city,
                            and click <span className="font-bold text-ink">Scan live SERP</span>.
                            We&apos;ll pull the live Google results and filter to any ads or
                            organic listings on <span className="font-bold text-ink">{domain}</span>.
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {result.paid_ads?.length > 0 ? (
                                <div>
                                    <div className="mono-label mb-2 text-red-800 flex items-center gap-1">
                                        <TargetIcon size={11} weight="bold" />
                                        {result.paid_ads.length} live paid ad{result.paid_ads.length === 1 ? "" : "s"}
                                    </div>
                                    <div className="space-y-2">
                                        {result.paid_ads.map((a, i) => (
                                            <div
                                                key={i}
                                                className="border-2 border-ink p-3 bg-white"
                                                data-testid={`snoop-paid-${i}`}
                                            >
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest font-bold bg-red-100 text-red-900 border border-red-300">
                                                        Ad
                                                    </span>
                                                    <a
                                                        href={a.url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-xs font-mono text-emerald-700 hover:underline flex items-center gap-1"
                                                    >
                                                        {a.display_url}
                                                        <ArrowSquareOut size={10} />
                                                    </a>
                                                </div>
                                                <SnoopCopyLine
                                                    text={a.title}
                                                    kind="headline"
                                                    domain={domain}
                                                    keyword={keyword}
                                                    testIdBase={`snoop-paid-${i}-headline`}
                                                />
                                                <SnoopCopyLine
                                                    text={a.description}
                                                    kind="description"
                                                    domain={domain}
                                                    keyword={keyword}
                                                    testIdBase={`snoop-paid-${i}-desc`}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="border border-amber-500 bg-amber-50 p-3 text-xs font-mono text-amber-900 flex items-start gap-2">
                                    <Warning size={12} weight="bold" className="mt-0.5 shrink-0" />
                                    Google isn&apos;t showing paid ads for this keyword at scrape
                                    time (SERP had {result.total_items_scanned} items). Try a broader
                                    keyword or rescan in a few hours — Google&apos;s ad load varies.
                                </div>
                            )}
                            {result.organic_matches?.length > 0 && (
                                <div>
                                    <div className="mono-label mb-2 flex items-center gap-1">
                                        <Info size={11} /> Organic listings for {domain}
                                    </div>
                                    <div className="space-y-2">
                                        {result.organic_matches.map((o, i) => (
                                            <div key={i} className="border border-neutral-300 p-3 bg-neutral-50">
                                                <div className="text-[10px] font-mono text-neutral-500">
                                                    #{o.position} · organic
                                                </div>
                                                <div className="font-heading font-bold text-ink text-sm leading-snug">
                                                    {o.title}
                                                </div>
                                                <div className="text-xs text-neutral-700 mt-1 leading-relaxed">
                                                    {o.snippet}
                                                </div>
                                                <a
                                                    href={o.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-[11px] font-mono text-klein hover:underline flex items-center gap-1 mt-1 break-all"
                                                >
                                                    {o.url}
                                                    <ArrowSquareOut size={9} />
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};


// ---- Snooped headline/description row + inline AI Variations ----
const ANGLE_META = {
    urgency: {
        label: "Urgency",
        cls: "border-red-400 bg-red-50 text-red-900",
        badge: "bg-red-100 text-red-900 border-red-300",
    },
    trust: {
        label: "Trust",
        cls: "border-emerald-400 bg-emerald-50 text-emerald-900",
        badge: "bg-emerald-100 text-emerald-900 border-emerald-300",
    },
    benefit: {
        label: "Benefit",
        cls: "border-klein bg-klein/5 text-ink",
        badge: "bg-klein/10 text-klein border-klein/30",
    },
};

const SnoopCopyLine = ({ text, kind, domain, keyword, testIdBase }) => {
    const [variations, setVariations] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    if (!text) return null;
    const isHeadline = kind === "headline";
    const charLimit = isHeadline ? 30 : 90;
    const textLen = text.length;

    const handlePin = (t, angle) => {
        if (!t) return;
        pushAdClipboard({
            text: t,
            kind,
            angle,
            source_domain: domain,
            source_keyword: keyword,
        });
        try {
            navigator.clipboard?.writeText(t);
        } catch {
            /* ignore */
        }
        toast.success(`Pinned to Ad Builder · ${angle ? ANGLE_META[angle]?.label : "Original"}`);
    };

    const handleGenerate = async () => {
        if (variations) {
            setOpen((v) => !v);
            return;
        }
        setLoading(true);
        const t = toast.loading("Claude generating variations…");
        try {
            const r = await generateAdVariations({ text, kind });
            setVariations(r);
            setOpen(true);
            toast.success("Variations ready", { id: t });
        } catch (e) {
            const msg = errText(e, "AI failed");
            toast.error(msg, { id: t });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={isHeadline ? "" : "mt-1"}>
            <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                    <div
                        className={
                            isHeadline
                                ? "font-heading font-bold text-klein text-base leading-snug"
                                : "text-xs text-ink leading-relaxed"
                        }
                    >
                        {text}
                    </div>
                    <div className="text-[9px] font-mono text-neutral-400 mt-0.5 uppercase tracking-widest">
                        {kind} · {textLen}/{charLimit} chars
                    </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                    <button
                        data-testid={`${testIdBase}-pin`}
                        onClick={() => handlePin(text, null)}
                        className="h-6 px-2 border border-neutral-400 hover:border-ink bg-white text-neutral-700 hover:text-ink font-mono uppercase tracking-widest text-[9px] font-bold flex items-center gap-1"
                        title="Pin this original copy to the Ad Builder clipboard"
                    >
                        <Copy size={10} weight="bold" /> Pin
                    </button>
                    <button
                        data-testid={`${testIdBase}-variations`}
                        onClick={handleGenerate}
                        disabled={loading}
                        className="h-6 px-2 border border-klein bg-klein text-white hover:bg-klein/90 font-mono uppercase tracking-widest text-[9px] font-bold flex items-center gap-1 disabled:opacity-50"
                        title="Generate 3 AI variations (Urgency · Trust · Benefit)"
                    >
                        {loading ? (
                            <ArrowsClockwise size={10} weight="bold" className="animate-spin" />
                        ) : (
                            <Sparkle size={10} weight="bold" />
                        )}
                        Variations
                    </button>
                </div>
            </div>
            {open && variations && (
                <div className="mt-2 space-y-1.5">
                    {["urgency", "trust", "benefit"].map((angle) => {
                        const v = variations[angle];
                        if (!v) return null;
                        const meta = ANGLE_META[angle];
                        return (
                            <div
                                key={angle}
                                className={`border ${meta.cls} p-2 flex items-start gap-2`}
                                data-testid={`${testIdBase}-var-${angle}`}
                            >
                                <span
                                    className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest font-bold border ${meta.badge} shrink-0`}
                                >
                                    {meta.label}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div
                                        className={
                                            isHeadline
                                                ? "font-heading font-bold text-ink text-sm leading-snug"
                                                : "text-xs text-ink leading-relaxed"
                                        }
                                    >
                                        {v}
                                    </div>
                                    <div className="text-[9px] font-mono text-neutral-500 mt-0.5">
                                        {v.length}/{charLimit} chars
                                    </div>
                                </div>
                                <button
                                    data-testid={`${testIdBase}-var-${angle}-pin`}
                                    onClick={() => handlePin(v, angle)}
                                    className="h-6 px-2 border border-ink bg-white hover:bg-ink hover:text-white text-ink font-mono uppercase tracking-widest text-[9px] font-bold flex items-center gap-1 shrink-0"
                                    title="Copy to Ad Builder"
                                >
                                    <Copy size={10} weight="bold" /> Copy
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ---- Δ-vs-previous banner for rehydrated Auction Insights ----
export const AuctionDeltaBanner = ({ delta }) => {
    if (!delta) return null;
    const youD = delta.you_is_delta;
    const leaderD = delta.leader_is_delta;
    if (youD == null && leaderD == null) return null;
    const prevDate = delta.previous_created_at
        ? new Date(delta.previous_created_at).toLocaleDateString(undefined, {
              day: "2-digit",
              month: "short",
              year: "numeric",
          })
        : "last scan";
    const Arrow = (v) =>
        v > 0.05 ? TrendUp : v < -0.05 ? TrendDown : Minus;
    const cls = (v) =>
        v > 0.05
            ? "text-emerald-800"
            : v < -0.05
            ? "text-red-800"
            : "text-neutral-600";
    return (
        <div
            data-testid="auction-delta-banner"
            className="border-2 border-ink bg-yellow-50 px-4 py-3 flex items-center gap-4 flex-wrap"
        >
            <div className="mono-label text-ink">Δ since last scan</div>
            {youD != null && (() => {
                const I = Arrow(youD);
                return (
                    <div className={`flex items-center gap-1.5 text-sm font-mono font-bold ${cls(youD)}`}>
                        <I size={13} weight="bold" />
                        You: {youD > 0 ? "+" : ""}
                        {youD.toFixed(1)}% IS
                        <span className="text-[10px] font-normal text-neutral-500 ml-1">
                            ({delta.you_is_previous?.toFixed(1) ?? "—"} → {delta.you_is_current?.toFixed(1) ?? "—"})
                        </span>
                    </div>
                );
            })()}
            {leaderD != null && delta.leader_domain && (() => {
                const I = Arrow(leaderD);
                return (
                    <div className={`flex items-center gap-1.5 text-sm font-mono font-bold ${cls(-leaderD)}`}>
                        <I size={13} weight="bold" />
                        Leader ({delta.leader_domain}): {leaderD > 0 ? "+" : ""}
                        {leaderD.toFixed(1)}% IS
                    </div>
                );
            })()}
            <div className="text-[11px] font-mono text-neutral-500 ml-auto">
                vs {prevDate}
                {delta.previous_filename ? ` · ${delta.previous_filename}` : ""}
            </div>
        </div>
    );
};
